<script setup lang="ts">
type BadgeVariant = 'notification' | 'neutral' | 'surface' | 'accent' | 'success' | 'danger' | 'warning' | 'info';
type BadgeAlias = 'primary' | 'secondary' | 'light' | 'none';
type BadgeShape = 'pill' | 'square';
type BadgeSize = 'default' | 'lg';

defineOptions({
    name: 'IdeoBadge',
    inheritAttrs: false
});

const props = withDefaults(defineProps<{
    variant?: BadgeVariant | BadgeAlias | string;
    shape?: BadgeShape;
    size?: BadgeSize;
    square?: boolean;
    dot?: boolean;
    status?: boolean;
    publication?: boolean;
    icon?: string | null;
    mono?: boolean;
    truncate?: boolean;
    tag?: string;
    background?: string | null;
    foreground?: string | null;
    borderColor?: string | null;
}>(), {
    variant: 'neutral',
    shape: 'pill',
    size: 'default',
    square: false,
    dot: false,
    status: false,
    publication: false,
    icon: null,
    mono: false,
    truncate: true,
    tag: 'span',
    background: null,
    foreground: null,
    borderColor: null
});

const attrs = useAttrs();
const baseClasses = 'inline-flex min-w-0 max-w-full items-center whitespace-nowrap rounded-full border py-0 font-[500] shadow-none focus-visible:outline-[2px] focus-visible:outline-offset-[2px] focus-visible:outline-accent';
const sizeClasses: Record<BadgeSize, string> = {
    default: 'h-[20px] gap-[4px] px-[8px] text-[0.6875rem]/none',
    lg: 'h-[24px] gap-[6px] px-[10px] text-[0.75rem]/none'
};
const variantClasses: Record<BadgeVariant, string> = {
    notification: '',
    surface: 'border-border bg-surface text-fg-muted dark:border-border-dark dark:bg-surface-dark dark:text-fg-muted-dark',
    neutral: 'border-border bg-surface-3 text-fg-muted dark:border-border-dark dark:bg-surface-3-dark dark:text-fg-muted-dark',
    accent: 'border-[color-mix(in_oklch,var(--color-accent)_30%,transparent)] bg-accent-soft text-accent-strong dark:border-[oklch(0.78_0.17_80/0.25)] dark:bg-[oklch(0.78_0.17_80/0.15)] dark:text-[oklch(0.78_0.17_80)]',
    success: 'border-[color-mix(in_oklch,var(--color-success)_30%,transparent)] bg-success-soft text-success dark:border-[oklch(0.65_0.16_145/0.25)] dark:bg-[oklch(0.65_0.16_145/0.15)] dark:text-[oklch(0.65_0.16_145)]',
    warning: 'border-[color-mix(in_oklch,var(--color-warning)_30%,transparent)] bg-warning-soft text-[oklch(0.58_0.13_68)] dark:border-[oklch(0.78_0.17_80/0.25)] dark:bg-[oklch(0.78_0.17_80/0.15)] dark:text-[oklch(0.78_0.17_80)]',
    info: 'border-[color-mix(in_oklch,var(--color-info)_30%,transparent)] bg-info-soft text-info dark:border-[oklch(0.65_0.15_240/0.25)] dark:bg-[oklch(0.65_0.15_240/0.15)] dark:text-[oklch(0.65_0.15_240)]',
    danger: 'border-[color-mix(in_oklch,var(--color-danger)_30%,transparent)] bg-danger-soft text-danger dark:border-[oklch(0.65_0.16_30/0.25)] dark:bg-[oklch(0.65_0.16_30/0.15)] dark:text-[oklch(0.65_0.16_30)]'
};
const variantAliases: Record<BadgeAlias, BadgeVariant> = {
    primary: 'accent',
    secondary: 'neutral',
    light: 'neutral',
    none: 'neutral'
};
const visualClassPrefixes = [
    'bg-',
    'border-',
    'decoration-',
    'font-',
    'gap-',
    'h-',
    'leading-',
    'outline-',
    'p-',
    'pb-',
    'pe-',
    'pl-',
    'pr-',
    'ps-',
    'pt-',
    'px-',
    'py-',
    'ring-',
    'rounded',
    'shadow',
    'size-',
    'text-',
    'tracking-',
    'w-'
];
const visualClassNames = new Set([
    'border',
    'capitalize',
    'italic',
    'line-through',
    'lowercase',
    'normal-case',
    'not-italic',
    'truncate',
    'underline',
    'uppercase',
    'whitespace-normal',
    'whitespace-nowrap'
]);

const resolvedVariant = computed<BadgeVariant>(() =>
{
    if (props.variant in variantClasses)
        return props.variant as BadgeVariant;

    if (props.variant in variantAliases)
        return variantAliases[props.variant as BadgeAlias];

    return 'neutral';
});
const resolvedShape = computed<BadgeShape>(() => props.square ? 'square' : props.shape);
const rootClasses = computed(() => [
    resolvedVariant.value === 'notification' ? 'box-content inline-flex h-[15px] min-w-[15px] shrink-0 items-center justify-center rounded-full border-[2px] border-surface bg-[var(--color-accent-solid)] px-[4px] text-[0.5625rem]/none font-[700] text-accent-fg dark:border-side-bg-dark' : [baseClasses, sizeClasses[props.size]],
    variantClasses[resolvedVariant.value],
    resolvedShape.value === 'square' ? 'rounded-[4px]' : '',
    props.mono ? 'font-mono' : '',
    attrsClasses.value
]);
const rootStyle = computed(() =>
{
    if (!props.background && !props.foreground && !props.borderColor)
        return null;

    return {
        backgroundColor: props.background || undefined,
        borderColor: props.borderColor || (props.background ? 'transparent' : undefined),
        color: props.foreground || (props.background ? '#ffffff' : undefined)
    };
});
const attrsClasses = computed(() => normalizeAttrClasses(attrs.class));
const rootAttrs = computed(() =>
{
    const result = { ...attrs };

    delete result.class;
    delete result.style;

    return result;
});

function getUtilityName(name: string)
{
    const utility = name.includes(':') ? name.slice(name.lastIndexOf(':') + 1) : name;

    return utility.startsWith('!') ? utility.slice(1) : utility;
}

function isAllowedAttrClassName(name: string)
{
    const utility = getUtilityName(name);

    if (visualClassNames.has(utility))
        return false;

    return !visualClassPrefixes.some(prefix => utility.startsWith(prefix));
}

function normalizeAttrClasses(value: any)
{
    if (!value)
        return {};

    if (Array.isArray(value))
    {
        const classes: Record<string, boolean> = {};

        value.forEach(item => Object.assign(classes, normalizeAttrClasses(item)));

        return classes;
    }

    if (typeof value === 'object')
    {
        const classes: Record<string, boolean> = {};

        Object.entries(value).forEach(([name, enabled]) =>
        {
            if (!enabled)
                return;

            Object.assign(classes, normalizeAttrClasses(name));
        });

        return classes;
    }

    const classes: Record<string, boolean> = {};

    String(value)
        .split(/\s+/u)
        .filter(name => name && isAllowedAttrClassName(name))
        .forEach((name) =>
        {
            classes[name] = true;
        });

    return classes;
}
</script>

<template>
    <component
        :is="props.tag"
        v-bind="rootAttrs"
        :class="rootClasses"
        :style="rootStyle"
        :data-badge-variant="resolvedVariant"
        :data-badge-shape="resolvedShape"
        :data-badge-size="props.size"
        :data-badge-status="props.status || undefined"
    >
        <span v-if="props.status" class="size-[6px] shrink-0 rounded-full bg-current" :class="props.publication ? '' : '[table_&]:hidden [[role=table]_&]:hidden [[role=grid]_&]:hidden'" aria-hidden="true" data-badge-dot />
        <IdeoIcon v-if="props.icon" :name="props.icon" :size="props.size === 'lg' ? 12 : 11" class="shrink-0" aria-hidden="true" data-badge-icon />
        <span v-if="props.truncate" class="min-w-0 overflow-hidden text-ellipsis whitespace-nowrap" data-badge-content>
            <slot name="default" />
        </span>
        <slot v-else name="default" />
    </component>
</template>
